'use client';

import { usePathname } from 'next/navigation';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

export type MobileHeaderVariant = 'standard' | 'large-title' | 'transparent';

export type PageChrome = {
  pathname: string;
  title: string;
  backHref?: string;
  backLabel?: string;
  mobileVariant?: MobileHeaderVariant;
  status?: ReactNode;
  trailingAction?: ReactNode;
  /** Focused editors replace the tab bar with their own safe-area footer. */
  hideMobileTabs?: boolean;
  hideDefaultActions?: boolean;
  /** Replaces the desktop AI/create top bar with back + trailingAction. */
  hideDesktopHeaderActions?: boolean;
};

type PageChromeContextValue = {
  chrome: PageChrome | null;
  register: (next: PageChrome) => () => void;
};

const PageChromeContext = createContext<PageChromeContextValue>({
  chrome: null,
  register: () => () => undefined
});

export function PageChromeProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const [chrome, setChrome] = useState<PageChrome | null>(null);

  const register = useCallback((next: PageChrome) => {
    setChrome(next);
    return () => {
      setChrome(current => (current === next ? null : current));
    };
  }, []);

  const value = useMemo(
    () => ({ chrome: chrome?.pathname === pathname ? chrome : null, register }),
    [chrome, pathname, register]
  );

  return (
    <PageChromeContext.Provider value={value}>
      {children}
    </PageChromeContext.Provider>
  );
}

export function usePageChrome() {
  return useContext(PageChromeContext);
}
